/**
 * LyricDraftCard — Collapsible card showing one agent's lyric draft.
 */

import React, { useState } from 'react';
import * as LucideIcons from 'lucide-react';
import { ChevronDown } from 'lucide-react';
import { Badge } from '@/components/ui';
import { LyricScoreBar } from './LyricScoreBar';
import type { LyricDraftProposal } from '@/types/lyricOrchestrator.types';
import type { LyricAgent } from '@/types/lyricOrchestrator.types';

interface LyricDraftCardProps {
  draft: LyricDraftProposal;
  agent: LyricAgent;
}

const IconLookup = LucideIcons as unknown as Record<string, React.FC<{ size?: number; className?: string }>>;

export const LyricDraftCard: React.FC<LyricDraftCardProps> = ({ draft, agent }) => {
  const [isOpen, setIsOpen] = useState(false);

  const IconComponent = IconLookup[agent.icon] || LucideIcons.HelpCircle;
  const borderColor = agent.color.replace('text-', 'border-');

  return (
    <div className={`border-l-4 ${borderColor} bg-slate-900/40 rounded-lg overflow-hidden`}>
      {/* Header — always visible */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-3 hover:bg-slate-800/30 transition-colors"
      >
        <div className="flex items-center gap-2">
          <IconComponent size={16} className={agent.color} />
          <span className="text-sm font-bold text-slate-200">{agent.name}</span>
          <Badge variant="default-muted" size="xs">{draft.scores.overall}/10</Badge>
          <span className="text-[10px] text-slate-500">
            conf: {(draft.confidence * 100).toFixed(0)}%
          </span>
        </div>
        <ChevronDown
          size={14}
          className={`text-slate-500 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Expanded content */}
      {isOpen && (
        <div className="px-3 pb-4 space-y-4 animate-in slide-in-from-top-2 duration-200">
          {/* Approach */}
          {draft.approach && (
            <p className="text-[11px] text-slate-400 italic border-l-2 border-slate-700 pl-2">
              {draft.approach}
            </p>
          )}

          {/* Score bars */}
          <div className="space-y-1.5">
            <LyricScoreBar label="Structure" score={draft.scores.structure} color="bg-emerald-500" />
            <LyricScoreBar label="Hook" score={draft.scores.hook} color="bg-rose-500" />
            <LyricScoreBar label="Imagery" score={draft.scores.imagery} color="bg-violet-500" />
            <LyricScoreBar label="Authenticity" score={draft.scores.authenticity} color="bg-amber-500" />
            <LyricScoreBar label="Emotional" score={draft.scores.emotionalImpact} color="bg-cyan-500" />
            <LyricScoreBar label="Overall" score={draft.scores.overall} color="bg-amber-400" />
          </div>

          {/* Strengths / Weaknesses */}
          <div className="grid grid-cols-2 gap-3">
            {draft.strengths.length > 0 && (
              <div>
                <h5 className="text-[10px] font-black text-green-400 uppercase tracking-widest mb-1">Strengths</h5>
                <ul className="space-y-0.5">
                  {draft.strengths.map((s, i) => (
                    <li key={i} className="text-[11px] text-slate-400">+ {s}</li>
                  ))}
                </ul>
              </div>
            )}
            {draft.weaknesses.length > 0 && (
              <div>
                <h5 className="text-[10px] font-black text-red-400 uppercase tracking-widest mb-1">Weaknesses</h5>
                <ul className="space-y-0.5">
                  {draft.weaknesses.map((w, i) => (
                    <li key={i} className="text-[11px] text-slate-400">- {w}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Full lyrics */}
          <div>
            <h5 className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2">Full Draft</h5>
            <pre className="text-[11px] text-slate-300 whitespace-pre-wrap font-sans leading-relaxed bg-slate-950/50 rounded-lg p-3 max-h-80 overflow-y-auto">
              {draft.lyrics}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
};
